import { useEffect, useRef, useState } from "react";
import { sendChat, submitQualifiedLead, trackFunnel } from "../../lib/api";

export const QUAL_STEPS = ["mode", "name", "email", "service", "budget", "timeline", "company", "phone", "description", "submitting", "cta"];
export const OPTIONAL_STEPS = ["company", "phone", "description"];

const SESSION_KEY = "jftm.chat.session";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const EMPTY_QUAL = { name: "", email: "", service: "", budget: "", timeline: "", company: "", phone: "", description: "" };

export const fill = (tpl, vars) => tpl.replace(/\{(\w+)\}/g, (_, k) => vars[k] ?? "");

// Anonymous chat session id. Kept in sessionStorage (expires with the tab)
// rather than localStorage so nothing persists beyond the browsing session.
const getOrCreateSessionId = () => {
  if (typeof window === "undefined") return `s_${Date.now()}`;
  let id = sessionStorage.getItem(SESSION_KEY);
  if (!id) {
    id = `s_${Math.random().toString(36).slice(2)}_${Date.now()}`;
    sessionStorage.setItem(SESSION_KEY, id);
  }
  return id;
};

/** All chatbot state + the guided lead-qualification state machine. */
export function useChatbot(t, locale) {
  const q = t.chatbot.qual;
  const [sessionId] = useState(getOrCreateSessionId);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [messages, setMessages] = useState([]);
  const [mode, setMode] = useState("greeting"); // greeting | ai | qual
  const [qualStep, setQualStep] = useState(0); // index into QUAL_STEPS
  const [qual, setQual] = useState(EMPTY_QUAL);
  const stepTimer = useRef(Date.now());

  // Initial greeting (also refreshes on locale change while the panel is untouched)
  useEffect(() => {
    setMessages((prev) => {
      if (prev.length === 0 || (prev.length === 1 && prev[0].kind === "greeting")) {
        return [{ role: "assistant", content: t.chatbot.greeting, kind: "greeting" }];
      }
      return prev;
    });
  }, [locale, t.chatbot.greeting]);

  const pushAssistant = (content) => setMessages((m) => [...m, { role: "assistant", content }]);
  const pushUser = (content) => setMessages((m) => [...m, { role: "user", content }]);

  const track = (step, previousStep, extra = {}) => {
    const now = Date.now();
    trackFunnel({
      session_id: sessionId,
      step,
      previous_step: previousStep || null,
      time_spent_ms: previousStep ? now - stepTimer.current : 0,
      locale,
      ...extra,
    });
    stepTimer.current = now;
  };

  /* ------------------------- Free-chat mode ------------------------- */
  const handleSendFree = async (text) => {
    pushUser(text);
    setInput("");
    setSending(true);
    try {
      const res = await sendChat(sessionId, text, locale);
      pushAssistant(res.reply);
    } catch (err) {
      console.warn("Chatbot: free-chat request failed", err);
      pushAssistant(locale === "de"
        ? "Es gab ein Problem. Bitte später erneut versuchen."
        : "There was an issue. Please try again later.");
    } finally {
      setSending(false);
    }
  };

  /* --------------------- Guided qualification flow --------------------- */
  const startQualification = () => {
    setMode("qual");
    setQualStep(1); // "name"
    pushUser(t.chatbot.chip_qual);
    pushAssistant(q.askName);
    track("name", "start");
  };

  const startAIChat = () => {
    setMode("ai");
    pushUser(t.chatbot.chip_ai);
    pushAssistant(locale === "de"
      ? "Perfekt — frag mich alles zu unseren Leistungen, Preisen, Prozessen oder AI-Setups."
      : "Perfect — ask me anything about our services, pricing, process or AI setups.");
  };

  const submitLead = async (final) => {
    setMode("qual");
    setQualStep(QUAL_STEPS.indexOf("submitting"));
    pushAssistant(q.submitting);
    track("submitting", "description");
    try {
      await submitQualifiedLead({
        session_id: sessionId,
        name: final.name,
        email: final.email,
        service: final.service,
        budget: final.budget,
        timeline: final.timeline,
        company: final.company || null,
        phone: final.phone || null,
        description: final.description || null,
        locale,
      });
      pushAssistant(fill(q.summary, { name: final.name }));
      pushAssistant(fill(q.summaryEmail, { email: final.email }));
      pushAssistant(q.submitted);
      pushAssistant(q.nextCta);
    } catch (err) {
      console.warn("Chatbot: qualified-lead submission failed", err);
      pushAssistant(q.failed);
    } finally {
      setQualStep(QUAL_STEPS.indexOf("cta"));
    }
  };

  const advanceOptional = (step, value) => {
    if (step === "company") {
      setQual((s) => ({ ...s, company: value }));
      setQualStep(QUAL_STEPS.indexOf("phone"));
      pushAssistant(q.askPhone);
      track("phone", "company");
    } else if (step === "phone") {
      setQual((s) => ({ ...s, phone: value }));
      setQualStep(QUAL_STEPS.indexOf("description"));
      pushAssistant(q.askDescription);
      track("description", "phone");
    } else if (step === "description") {
      const finalQual = { ...qual, description: value };
      setQual(finalQual);
      submitLead(finalQual);
    }
  };

  const handleQualText = (raw) => {
    const text = (raw || "").trim();
    if (!text) return;
    const step = QUAL_STEPS[qualStep];

    if (step === "name") {
      pushUser(text);
      const name = text.replace(/\s+/g, " ");
      setQual((s) => ({ ...s, name }));
      setInput("");
      setQualStep(QUAL_STEPS.indexOf("email"));
      pushAssistant(fill(q.askEmail, { name: name.split(" ")[0] }));
      track("email", "name");
      return;
    }
    if (step === "email") {
      pushUser(text);
      setInput("");
      if (!EMAIL_RE.test(text)) {
        pushAssistant(q.invalidEmail);
        return;
      }
      setQual((s) => ({ ...s, email: text }));
      setQualStep(QUAL_STEPS.indexOf("service"));
      pushAssistant(q.askService);
      track("service", "email");
      return;
    }
    if (OPTIONAL_STEPS.includes(step)) {
      pushUser(text);
      setInput("");
      advanceOptional(step, text);
    }
    // service / budget / timeline are chip-driven only; typed input is ignored on those steps
  };

  const handleSkip = () => {
    const step = QUAL_STEPS[qualStep];
    if (!OPTIONAL_STEPS.includes(step)) return;
    pushUser(q.skip);
    setInput("");
    advanceOptional(step, "");
  };

  const handleChip = (value) => {
    const step = QUAL_STEPS[qualStep];
    pushUser(value);
    if (step === "service") {
      setQual((s) => ({ ...s, service: value }));
      setQualStep(QUAL_STEPS.indexOf("budget"));
      pushAssistant(q.askBudget);
      track("budget", "service", { service_selected: value });
    } else if (step === "budget") {
      setQual((s) => ({ ...s, budget: value }));
      setQualStep(QUAL_STEPS.indexOf("timeline"));
      pushAssistant(q.askTimeline);
      track("timeline", "budget", { budget_selected: value });
    } else if (step === "timeline") {
      setQual((s) => ({ ...s, timeline: value }));
      setQualStep(QUAL_STEPS.indexOf("company"));
      pushAssistant(q.askCompany);
      track("company", "timeline", { timeline_selected: value });
    }
  };

  const restart = () => {
    setMode("greeting");
    setQualStep(0);
    setQual(EMPTY_QUAL);
    setMessages([{ role: "assistant", content: t.chatbot.greeting, kind: "greeting" }]);
    setInput("");
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (sending) return;
    if (mode === "qual") {
      const step = QUAL_STEPS[qualStep];
      if (step === "name" || step === "email" || OPTIONAL_STEPS.includes(step)) handleQualText(input);
      return;
    }
    const text = input.trim();
    if (!text) return;
    handleSendFree(text);
  };

  /* --------------------------- Derived state --------------------------- */
  const currentStep = QUAL_STEPS[qualStep];
  const inputDisabled = (
    sending ||
    (mode === "qual" && ["service", "budget", "timeline", "submitting", "cta"].includes(currentStep))
  );
  const placeholders = {
    name: locale === "de" ? "Dein Vorname..." : "Your first name...",
    email: "you@company.com",
    company: locale === "de" ? "Unternehmen (optional)..." : "Company (optional)...",
    phone: locale === "de" ? "Telefonnummer (optional)..." : "Phone number (optional)...",
    description: locale === "de" ? "Kurze Projektbeschreibung (optional)..." : "Short project description (optional)...",
  };
  const inputPlaceholder = (mode === "qual" && placeholders[currentStep]) || t.chatbot.placeholder;

  return {
    q,
    messages,
    input,
    setInput,
    sending,
    mode,
    qualStep,
    qual,
    currentStep,
    inputDisabled,
    inputPlaceholder,
    showGreetingChips: mode === "greeting" && messages.length <= 1,
    showServiceChips: mode === "qual" && currentStep === "service",
    showBudgetChips: mode === "qual" && currentStep === "budget",
    showTimelineChips: mode === "qual" && currentStep === "timeline",
    showSkipChip: mode === "qual" && OPTIONAL_STEPS.includes(currentStep),
    showEndCta: mode === "qual" && currentStep === "cta",
    startQualification,
    startAIChat,
    handleChip,
    handleSkip,
    handleSubmit,
    restart,
  };
}
